/**
 * 69.10 step 5: the lighter drawn in play (lane F's run, stock and unload: core/lighter.ts lighterPose, state.lighters,
 * state.lighterStrips). Drawn only: everything here reads the state and the pure pose, nothing is written back.
 *   - **The lighter** (lighter A, the lifting body: rendering/orbitShips.ts lifter, built with its ramp left out): its hull
 *     at the pose (the belly's point, the surface's up, the heading, the pitch, nose up positive), the full tier within
 *     LIGHTER_FULL_PEOPLE of the camera and the far tier beyond; its ramp hinged at the bay's sill (shut: up, closing the
 *     bay; down: its lip on the ground); its four swivelling nacelles (the owner: "I think it only makes sense if we have
 *     downward articulating thrusters to justify it being vtol/stovl ish") turned by `nacelleTilt` — aft in the glide and the
 *     climb, swinging down through the approach to the flare and the touchdown, back aft as the bells take over — their jets
 *     by `fans`; the three bells' flames (by `bells`); dust thrown off the ground under the jets near it, a smoke trail behind the bells on the
 *     ascent (both in the explosion field's pools: no draws of their own).
 *   - **Its frames:** the run's load (`load`, a frame and its vehicle each) in the bay's four slots, rolling out down the
 *     ramp one by one as `aboard` falls — the k-th over the frameOutSeconds before it is out — onto the apron behind the
 *     ship, where they wait for the robots (or, with nothing staged, till the ship is gone).
 *   - **The strip** (state.lighterStrips): paving draped on the drawn ground, 60 × 32 people, its edges, its ends, a dashed
 *     centre line, the touchdown box and the apron box marked.
 *   - **The carry** (state.lighters.staged, one frame at a time): the corner robots (rendering/motorPool.ts) arrive, clamp and
 *     lift the frame on the apron (the pick), walk it round the strip, through the pool's back gate, down its passage and
 *     along a lane (the walk), set it down by the stacker arm; the arm (driven there during the walk) lifts it, swings and sets
 *     it on the yard's next free stack (the stack), where the motor pool's look then keeps it (MotorPools.refresh). The pick,
 *     the walk and the stack are the item's own ticks' proportions (pick 5 s, stack 10 s, the walk between).
 * Units: the models are the kit's (a person 1.8), scaled to the match's person; the strip and the poses are the world's.
 */
import * as THREE from 'three';
import type { GameState, LighterRun, MotorPoolPlan, PlanetState, Vec3, VehicleKind } from '../types';
import { LIGHTER, LIGHTER_UNLOAD, PHYS_DT, TEAMS } from '../constants';
import { lighterNow } from '../core/shipNow';
import { MOTOR_POOL, planCentre } from '../core/motorPool';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { buildShipLook, hullName, FRAME, LIGHTER_SLOTS, LIFTER_NACELLES, lifterNacelle, mergedTier, Spec, type Livery } from './orbitShips';
import { buildVehicleFrame, FRAME_DECK } from './vehicleFrame';
import { buildHandler } from './motorPool';
import { folded, vehicleModel, nextFreeSpot, stackPoint, type MotorPools } from './motorPoolPlay';
import { unitPaletteFor, type UnitPalette } from './unitPalette';
import type { ExplosionField } from './explosionLook';

/** The kit's person (metres). */
const U = 1.8;
/** Within this many people of the camera the lighter draws its full tier (and its frames, its ramp); its far tier beyond. */
export const LIGHTER_FULL_PEOPLE = 350;
/** The ramp (metres, the lifter's frame): its plate, the hinge at the bay's sill. */
const RAMP = { width: 12.4, length: 7.6, thick: 0.4, hingeY: 0.8, hingeZ: -18 };
/** The apron behind the ship (metres, the lighter's frame on its strip): where the k-th frame waits. */
export const APRON = { z: -34, pitch: 4.6 };
export const apronSpot = (k: number): { x: number; z: number } => ({ x: (k - 1.5) * APRON.pitch, z: APRON.z });
/** The bay's slots in the order the frames roll out: the rear pair first. */
export const ROLL_ORDER = [...LIGHTER_SLOTS.lifter].sort((a, b) => a.z - b.z || a.x - b.x);
const ticksOf = (seconds: number) => Math.max(1, Math.round(seconds / PHYS_DT));

const _u = new THREE.Vector3(), _f = new THREE.Vector3(), _r = new THREE.Vector3(), _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _x = new THREE.Vector3(1, 0, 0);

/**
 * How far the nacelles are swung down (0 aft … 1 down) for a pose: lane F's `nozzle` (69.10's STOVL) — aft in the unpowered
 * glide, swinging down through the approach, down for the vertical descent, on the deck and the vertical lift, then back aft
 * along the thrust vector's angle as the climb flattens with the hull near level and the bells take over.
 */
export function nacelleTilt(pose: { nozzle: number }): number {
  return Math.max(0, Math.min(1, pose.nozzle));
}
/** The nacelles' shafts in the lifter's frame (metres): the fore and aft pair each side. */
export const NACELLE_PIVOTS = [-1, 1].flatMap(sx => LIFTER_NACELLES.z.map(z => new THREE.Vector3(sx * LIFTER_NACELLES.x, LIFTER_NACELLES.y, z)));
/** A nacelle's place at its shaft, swung down by `tilt` (and its jet stretched by `jet`, when given). */
export function nacelleMatrix(pivot: THREE.Vector3, tilt: number, jet?: number, out = new THREE.Matrix4()): THREE.Matrix4 {
  out.makeRotationX(-tilt * Math.PI / 2).setPosition(pivot);
  if (jet !== undefined) out.multiply(_m.makeScale(1, 1, 0.25 + 0.75 * jet));
  return out;
}

/** A pose's turn: +y to `up`, +z (the nose) along `heading` made level with the ground there, then pitched nose up by `pitch`. */
export function lighterQuaternion(up: Vec3, heading: Vec3, pitch: number, out = new THREE.Quaternion()): THREE.Quaternion {
  _u.set(up.x, up.y, up.z).normalize();
  _f.set(heading.x, heading.y, heading.z).addScaledVector(_u, -(heading.x * _u.x + heading.y * _u.y + heading.z * _u.z)).normalize();
  _r.crossVectors(_u, _f);
  out.setFromRotationMatrix(_m.makeBasis(_r, _u, _f));
  return out.multiply(_q.setFromAxisAngle(_x, -pitch));
}

/**
 * Where the k-th frame (in roll-out order) stands at share `f` of its roll (0 in its slot … 1 on its apron spot), in the
 * lighter's frame (metres): out of the bay along its slot's line, down the ramp to its foot, across to its spot.
 */
export function rollPoint(k: number, f: number): THREE.Vector3 {
  const s = ROLL_ORDER[k % ROLL_ORDER.length], a = apronSpot(k);
  const pts = [new THREE.Vector3(s.x, s.y, s.z), new THREE.Vector3(s.x, s.y, RAMP.hingeZ - 0.5), new THREE.Vector3(s.x * 0.6, 0, RAMP.hingeZ - RAMP.length - 1.5), new THREE.Vector3(a.x, 0, a.z)];
  return alongPolyline(pts, Math.max(0, Math.min(1, f)));
}

/** A point `f` of the way along a polyline (by its length). */
export function alongPolyline(pts: readonly THREE.Vector3[], f: number): THREE.Vector3 {
  let total = 0;
  for (let i = 1; i < pts.length; i++) total += pts[i].distanceTo(pts[i - 1]);
  let left = Math.max(0, Math.min(1, f)) * total;
  for (let i = 1; i < pts.length; i++) {
    const d = pts[i].distanceTo(pts[i - 1]);
    if (left <= d || i === pts.length - 1) return pts[i - 1].clone().lerp(pts[i], d > 0 ? Math.min(1, left / d) : 1);
    left -= d;
  }
  return pts[0].clone();
}

/** A staged frame's carry, by its own ticks: the pick (LIGHTER_UNLOAD.pickSeconds), the walk, the stack (stackSeconds). */
export function carryStage(f: { lift: number; stacked: number }, tick: number): { stage: 'wait' | 'pick' | 'walk' | 'stack' | 'done'; share: number } {
  if (tick < f.lift) return { stage: 'wait', share: 0 };
  if (tick >= f.stacked) return { stage: 'done', share: 1 };
  const pick = f.lift + ticksOf(LIGHTER_UNLOAD.pickSeconds), stack = Math.max(pick, f.stacked - ticksOf(LIGHTER_UNLOAD.stackSeconds));
  if (tick < pick) return { stage: 'pick', share: (tick - f.lift) / (pick - f.lift) };
  if (tick < stack) return { stage: 'walk', share: (tick - pick) / Math.max(1, stack - pick) };
  return { stage: 'stack', share: (tick - stack) / Math.max(1, f.stacked - stack) };
}

/**
 * Where the stacker arm works a stack (the yard's frame, the kit's units): the target's floor centre `T`, the way the arm
 * faces it `face` (along z, from the lane beside its row), its pedestal (6.5 m back from the target), and the drop `D` where
 * the robots set the frame down for it (in the same lane, 6.5 m from the pedestal toward the passage).
 */
export function armWork(plan: MotorPoolPlan, spot: { slot: number; tier: number }): { T: THREE.Vector3; face: THREE.Vector3; pedestal: THREE.Vector3; drop: THREE.Vector3; lane: number } {
  const T = stackPoint(plan, spot), lanes = plan.lanes.filter(l => l.cols > 3);
  const zOf = (l: (typeof lanes)[number]) => planCentre(plan, l.i, l.j, l.cols, l.rows).z * U;
  const lane = lanes.reduce((best, l, i) => Math.abs(zOf(l) - T.z) < Math.abs(zOf(lanes[best]) - T.z) ? i : best, 0);
  const face = new THREE.Vector3(0, 0, Math.sign(T.z - zOf(lanes[lane])) || 1);
  const pedestal = new THREE.Vector3(T.x, 0, T.z).addScaledVector(face, -REACH);
  const drop = pedestal.clone().add(new THREE.Vector3(pedestal.x > 0 ? -REACH : REACH, 0, 0));
  return { T, face, pedestal, drop, lane };
}
const REACH = 6.5;
/** The arm's yaw to face `dir` (its +z along it). */
const yawOf = (dir: THREE.Vector3) => Math.atan2(dir.x, dir.z);

/** A frame with its vehicle (one group: the frame's mesh and the vehicle folded), from a team's cache. */
interface Kit { frame: { geo: THREE.BufferGeometry; mat: THREE.Material }; cars: Map<VehicleKind, { geo: THREE.BufferGeometry; mat: THREE.Material }>; p: UnitPalette; livery: Livery }

interface ShipView {
  root: THREE.Group; full: THREE.Mesh; far: THREE.Mesh; ramp: THREE.Group; nacelles: THREE.InstancedMesh; nacellesFar: THREE.InstancedMesh; jets: THREE.InstancedMesh; bells: THREE.Group;
  frames: THREE.Group[]; trail: number; dust: number;
}
interface CarryView {
  bots: THREE.Mesh[]; arm: THREE.Group; armMesh: THREE.Mesh | null; frame: THREE.Group | null; item: number;
  path: THREE.Vector3[]; armPath: THREE.Vector3[]; armAt: THREE.Vector3; armYaw: number; work: ReturnType<typeof armWork> | null;
  poses: { low: THREE.BufferGeometry; high: THREE.BufferGeometry; set: THREE.BufferGeometry } | null; apron: Map<number, THREE.Group>; spots: Map<number, number>;
}

/** A static draped mesh (the strip's paving and its marks) from world vertices and colours. */
function draped(pos: number[], col: number[], index: number[], name: string): THREE.Mesh {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.setIndex(index); g.computeVertexNormals();
  const m = new THREE.Mesh(g, new THREE.MeshLambertMaterial({ vertexColors: true, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 }));
  m.name = name; m.matrixAutoUpdate = false;
  return m;
}

/**
 * The lighters in play: a view per run, a strip per side, a carry per side. Made by the host when GameConfig.lighters is on;
 * `update` once a frame.
 */
export class LighterViews {
  private readonly ships = new Map<number, ShipView>();
  private readonly strips = new Map<number, THREE.Mesh[]>();
  private readonly carries = new Map<number, CarryView>();
  private readonly kits = new Map<number, Kit>();
  private readonly hulls = new Map<number, { full: THREE.BufferGeometry; far: THREE.BufferGeometry; mat: THREE.Material; nacelle: THREE.BufferGeometry; nacelleFar: THREE.BufferGeometry }>();
  private readonly plumes: { jets: THREE.BufferGeometry; bells: THREE.BufferGeometry; jetMat: THREE.MeshBasicMaterial; bellMat: THREE.MeshBasicMaterial; ramp: THREE.BufferGeometry; rampMat: THREE.Material };
  private readonly person: number;

  constructor(private readonly scene: THREE.Scene, state: GameState) {
    this.person = PERSON_BODY_HEIGHT * personSetOf(state).k;
    // a nacelle's jet: an open cone aft from its nozzle (in the nacelle's frame: turned with it); the bells' flames: three cones aft
    const k = LIFTER_NACELLES.scale, jet = new THREE.ConeGeometry(0.33 * k, 5 * k, 10, 1, true).translate(0, 2.5 * k, 0).rotateX(-Math.PI / 2).translate(0, 0, -1.62 * k);
    const flames: THREE.BufferGeometry[] = [];
    for (const x of [-3, 0, 3]) flames.push(new THREE.ConeGeometry(1.5, 26, 12, 1, true).translate(0, 13, 0).rotateX(-Math.PI / 2).translate(x, 0, 0));
    const merge = (list: THREE.BufferGeometry[]) => { const g = new THREE.BufferGeometry(), pos: number[] = [], index: number[] = []; for (const l of list) { const n = pos.length / 3, p = l.getAttribute('position'), ix = l.getIndex(); for (let i = 0; i < p.count; i++) pos.push(p.getX(i), p.getY(i), p.getZ(i)); if (ix) for (let i = 0; i < ix.count; i++) index.push(n + ix.getX(i)); l.dispose(); } g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3)); g.setIndex(index); return g; };
    this.plumes = {
      jets: jet, bells: merge(flames),
      jetMat: new THREE.MeshBasicMaterial({ color: '#d6e8f7', transparent: true, opacity: 0.22, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide }),
      bellMat: new THREE.MeshBasicMaterial({ color: '#ffcf7a', transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, toneMapped: false }),
      ramp: new THREE.BoxGeometry(RAMP.width, RAMP.thick, RAMP.length).translate(0, 0, -RAMP.length / 2),
      rampMat: new THREE.MeshLambertMaterial({ color: '#7f6448' }),
    };
  }

  /** What the views draw now: draws and triangles (the ships in flight, the strips, the carries). */
  get cost(): { draws: number; triangles: number } {
    let draws = 0, triangles = 0;
    this.scene.traverseVisible(o => {
      if (!o.name.startsWith('lighter:')) return;
      o.traverseVisible(c => { const m = c as THREE.Mesh; if (!m.isMesh) return; draws++; const g = m.geometry; triangles += (g.index ? g.index.count : g.attributes.position.count) / 3; });
    });
    return { draws, triangles };
  }

  private kit(team: number): Kit {
    let k = this.kits.get(team);
    if (!k) {
      const t = TEAMS[team % TEAMS.length], p = unitPaletteFor(t), livery = { name: hullName(t.name), mark: 0 }, f = buildVehicleFrame(p, livery, { variant: 'open', number: 10 + team });
      k = { frame: { geo: f.mesh.geometry, mat: f.mesh.material as THREE.Material }, cars: new Map(), p, livery };
      this.kits.set(team, k);
    }
    return k;
  }
  /** A frame and its vehicle, in the kit's units (its floor's middle at the origin, its length along z). */
  private frameOf(team: number, kind: VehicleKind | undefined): THREE.Group {
    const k = this.kit(team), g = new THREE.Group();
    g.add(new THREE.Mesh(k.frame.geo, k.frame.mat));
    if (kind) {
      let car = k.cars.get(kind);
      if (!car) { const m = vehicleModel(kind, k.p); m.silhouette.visible = false; car = { geo: folded(m.root), mat: new THREE.MeshLambertMaterial({ vertexColors: true }) }; k.cars.set(kind, car); }
      const v = new THREE.Mesh(car.geo, car.mat); v.position.y = FRAME_DECK; g.add(v);
    }
    return g;
  }

  private hull(team: number): { full: THREE.BufferGeometry; far: THREE.BufferGeometry; mat: THREE.Material; nacelle: THREE.BufferGeometry; nacelleFar: THREE.BufferGeometry } {
    let h = this.hulls.get(team);
    if (!h) {
      const k = this.kit(team), look = buildShipLook('lifter', k.p, k.livery, { cargo: false, ramp: false, nacelles: false });
      const ns = new Spec(k.livery); lifterNacelle(ns, 0, 0, 0, 0);
      const nacelle = mergedTier(ns, k.p, false), nacelleFar = mergedTier(ns, k.p, true);
      for (const g of [look.full, look.far, nacelle, nacelleFar]) { g.setAttribute('color', g.getAttribute('paint')); g.deleteAttribute('glow'); }
      h = { full: look.full, far: look.far, mat: new THREE.MeshLambertMaterial({ vertexColors: true }), nacelle, nacelleFar };
      this.hulls.set(team, h);
    }
    return h;
  }

  private ship(r: LighterRun): ShipView {
    let v = this.ships.get(r.id);
    if (!v) {
      const h = this.hull(r.team), root = new THREE.Group();
      root.name = `lighter:${r.id}`;
      root.scale.setScalar(this.person / U);
      const full = new THREE.Mesh(h.full, h.mat), far = new THREE.Mesh(h.far, h.mat);
      const ramp = new THREE.Group(); ramp.position.set(0, RAMP.hingeY, RAMP.hingeZ); ramp.add(new THREE.Mesh(this.plumes.ramp, this.plumes.rampMat));
      const nacelles = new THREE.InstancedMesh(h.nacelle, h.mat, 4);
      nacelles.name = `lighter:nacelles:${r.id}`;
      const nacellesFar = new THREE.InstancedMesh(h.nacelleFar, h.mat, 4);
      nacellesFar.name = `lighter:nacelles:far:${r.id}`;
      const jets = new THREE.InstancedMesh(this.plumes.jets, this.plumes.jetMat, 4);
      jets.name = `lighter:jets:${r.id}`;
      for (const m of [nacelles, nacellesFar, jets]) m.frustumCulled = false;
      const bells = new THREE.Group(); bells.position.set(0, 8.6, -21); bells.add(new THREE.Mesh(this.plumes.bells, this.plumes.bellMat));
      const frames = r.load.map(kind => this.frameOf(r.team, kind));
      root.add(full, far, ramp, nacelles, nacellesFar, jets, bells, ...frames);
      this.scene.add(root);
      v = { root, full, far, ramp, nacelles, nacellesFar, jets, bells, frames, trail: 0, dust: 0 };
      this.ships.set(r.id, v);
    }
    return v;
  }

  /** The side's strip: paving draped on the drawn ground and its marks (two draws), made once. */
  private strip(state: GameState, team: number, groundAt: (p: PlanetState, d: Vec3) => number): void {
    if (this.strips.has(team)) return;
    const s = state.lighterStrips?.[team], planet = s ? state.planets[s.planetIndex] : undefined;
    if (!s || !planet) return;
    const P = this.person, up = new THREE.Vector3(s.dir.x, s.dir.y, s.dir.z).normalize(), fwd = new THREE.Vector3(s.front.x, s.front.y, s.front.z), R = planet.radius;
    fwd.addScaledVector(up, -fwd.dot(up)).normalize();
    const right = new THREE.Vector3().crossVectors(up, fwd), pc = new THREE.Vector3(planet.position.x, planet.position.y, planet.position.z);
    // a point `x` across and `z` along (people) on the drawn ground, `lift` people over it
    const on = (x: number, z: number, lift: number): THREE.Vector3 => {
      const d = up.clone().multiplyScalar(R).addScaledVector(right, x * P).addScaledVector(fwd, z * P).normalize();
      return pc.clone().addScaledVector(d, groundAt(planet, { x: d.x, y: d.y, z: d.z }) + lift * P);
    };
    const A = LIGHTER.strip.alongPeople / 2, B = LIGHTER.strip.acrossPeople / 2;
    // the paving: a 13 × 7 grid draped on the ground, a little lighter down the middle
    const pos: number[] = [], col: number[] = [], index: number[] = [], NA = 13, NB = 7, pave = new THREE.Color('#3d3f42'), mid = new THREE.Color('#47494c');
    for (let i = 0; i < NA; i++) for (let j = 0; j < NB; j++) {
      const p = on(-B + (2 * B * j) / (NB - 1), -A + (2 * A * i) / (NA - 1), 0.04);
      pos.push(p.x, p.y, p.z);
      const c = j === 0 || j === NB - 1 ? pave : mid; col.push(c.r, c.g, c.b);
      if (i > 0 && j > 0) { const a = (i - 1) * NB + j - 1, b = a + 1, c2 = i * NB + j - 1, d = c2 + 1; index.push(a, c2, b, b, c2, d); }
    }
    const paving = draped(pos, col, index, `lighter:strip:${team}`);
    // the marks: quads along a line, every ~3 people draped
    const mp: number[] = [], mc: number[] = [], mi: number[] = [];
    const line = (x0: number, z0: number, x1: number, z1: number, w: number, color: string) => {
      const c = new THREE.Color(color), len = Math.hypot(x1 - x0, z1 - z0), n = Math.max(1, Math.ceil(len / 3)), nx = -(z1 - z0) / len * w / 2, nz = (x1 - x0) / len * w / 2;
      for (let k = 0; k <= n; k++) {
        const t = k / n, x = x0 + (x1 - x0) * t, z = z0 + (z1 - z0) * t, base = mp.length / 3;
        for (const sgn of [-1, 1]) { const p = on(x + sgn * nx, z + sgn * nz, 0.07); mp.push(p.x, p.y, p.z); mc.push(c.r, c.g, c.b); }
        if (k > 0) mi.push(base - 2, base, base - 1, base - 1, base, base + 1);
      }
    };
    const box = (x0: number, z0: number, x1: number, z1: number, w: number, color: string) => { line(x0, z0, x1, z0, w, color); line(x1, z0, x1, z1, w, color); line(x1, z1, x0, z1, w, color); line(x0, z1, x0, z0, w, color); };
    box(-B + 1, -A + 1, B - 1, A - 1, 0.5, '#d8d4c8');   // (the edges)
    for (let z = -A + 4; z < A - 4; z += 6) line(0, z, 0, z + 3, 0.35, '#d8d4c8');   // (the dashed centre line)
    for (const z of [-A + 2, A - 2]) for (let x = -B + 4; x <= B - 4; x += 3) line(x, z - 1, x, z + 1, 0.6, '#d8d4c8');   // (the ends' bars)
    box(-6.5, -13, 6.5, 13, 0.4, '#d6b43a');   // (the touchdown box round the lighter: 42 × 17 m)
    box(-11, -22, 11, -16, 0.35, '#d6b43a');   // (the apron box behind it)
    const marks = draped(mp, mc, mi, `lighter:strip:marks:${team}`);
    this.scene.add(paving, marks);
    this.strips.set(team, [paving, marks]);
  }

  /** The lighter's frame on its strip (the ship down there): a point in metres → the world, dropped onto the drawn ground. */
  private onStrip(state: GameState, team: number, x: number, z: number, groundAt: (p: PlanetState, d: Vec3) => number): THREE.Vector3 | null {
    const s = state.lighterStrips?.[team], planet = s ? state.planets[s.planetIndex] : undefined;
    if (!s || !planet) return null;
    const k = this.person / U, up = new THREE.Vector3(s.dir.x, s.dir.y, s.dir.z).normalize(), fwd = new THREE.Vector3(s.front.x, s.front.y, s.front.z);
    fwd.addScaledVector(up, -fwd.dot(up)).normalize();
    const right = new THREE.Vector3().crossVectors(up, fwd);   // (the lighter's +x: up × its nose)
    const d = up.clone().multiplyScalar(planet.radius).addScaledVector(right, x * k).addScaledVector(fwd, z * k).normalize();
    return new THREE.Vector3(planet.position.x, planet.position.y, planet.position.z).addScaledVector(d, groundAt(planet, { x: d.x, y: d.y, z: d.z }));
  }

  /** Once a frame. */
  update(state: GameState, tick: number, dt: number, camera: THREE.Vector3, groundAt: (p: PlanetState, d: Vec3) => number, pools: MotorPools | null, field: ExplosionField | null): void {
    const P = this.person, runs = state.lighters?.runs ?? [], seen = new Set<number>(), frameTicks = ticksOf(LIGHTER.frameOutSeconds);
    for (const t of Object.keys(state.lighterStrips ?? {})) this.strip(state, Number(t), groundAt);
    for (const r of runs) {
      const pose = lighterNow(state, r, tick)?.lighter!.pose;   // 69.17 step 1: the ship as it is now
      if (!pose) continue;
      seen.add(r.id);
      const v = this.ship(r);
      v.root.position.set(pose.pos.x, pose.pos.y, pose.pos.z);
      lighterQuaternion(pose.dir, pose.heading, pose.pitch, v.root.quaternion);
      const people = v.root.position.distanceTo(camera) / P, near = people < LIGHTER_FULL_PEOPLE;
      v.full.visible = near; v.far.visible = !near; v.ramp.visible = near;
      v.ramp.rotation.x = Math.PI / 2 + (-Math.asin(RAMP.hingeY / RAMP.length) - Math.PI / 2) * pose.ramp;   // (up, shutting the bay → its lip on the ground)
      // the nacelles swung down by the tilt, their jets by the fans' share (none on the ground)
      const tilt = nacelleTilt(pose), jet = pose.fans;   // (the jets by the thrusters' share: none on the deck)
      NACELLE_PIVOTS.forEach((pv, i) => { const m = nacelleMatrix(pv, tilt); v.nacelles.setMatrixAt(i, m); v.nacellesFar.setMatrixAt(i, m); v.jets.setMatrixAt(i, nacelleMatrix(pv, tilt, jet)); });
      for (const m of [v.nacelles, v.nacellesFar, v.jets]) m.instanceMatrix.needsUpdate = true;
      v.nacelles.visible = near; v.nacellesFar.visible = !near; v.jets.visible = jet > 0.02;
      v.bells.visible = pose.bells > 0.02; v.bells.scale.set(1, 1, 0.3 + 0.7 * pose.bells);
      // the frames: aboard in their slots, rolling out down the ramp, on the apron (drawn there by the carry once staged)
      const staged = new Set((state.lighters?.staged?.[r.team] ?? []).map(f => f.out));
      v.frames.forEach((g, k) => {
        const out = r.rampTick + (k + 1) * frameTicks;
        const share = tick < out - frameTicks ? 0 : tick >= out ? 1 : Math.min(1, (tick - (out - frameTicks)) / (0.8 * frameTicks));
        if (tick >= out && staged.has(out)) { g.visible = false; return; }   // (the carry has it)
        g.visible = near || share > 0;
        if (share >= 1 && tick >= r.liftTick) {   // (on the apron with nothing staged: it stays there while the ship goes)
          const w = this.onStrip(state, r.team, apronSpot(k).x, apronSpot(k).z, groundAt);
          if (w) { g.position.copy(v.root.worldToLocal(w)); g.quaternion.copy(v.root.quaternion).invert().multiply(lighterQuaternion(pose.dir, state.lighterStrips![r.team].front, 0)); }
          return;
        }
        g.position.copy(rollPoint(k, share)); g.quaternion.identity();
      });
      // dust off the ground under the jets, swung down, near it; a smoke trail behind the bells on the ascent
      if (field && people < 2500) {
        if (pose.fans > 0.3 && nacelleTilt(pose) > 0.5 && pose.height < 25 * P) {
          v.dust += dt;
          while (v.dust > 0.08) {
            v.dust -= 0.08;
            const a = Math.random() * Math.PI * 2, w = this.onStrip(state, r.team, Math.cos(a) * 14, Math.sin(a) * 20, groundAt);
            if (w) { const out = w.clone().sub(v.root.position).normalize().multiplyScalar(6 * P); field.emit('dust', { at: w, vel: out, up: pose.dir, life: 2.2, s0: 0.6 * P, s1: 3.5 * P, c0: '#a59b8a', drag: 1.4, flat: true }); }
          }
        }
        if (pose.bells > 0.5) {
          v.trail += dt;
          while (v.trail > 0.12) {
            v.trail -= 0.12;
            const w = v.root.localToWorld(new THREE.Vector3((Math.random() - 0.5) * 3, 8.6, -34)), aft = v.root.localToWorld(new THREE.Vector3(0, 8.6, -60)).sub(w).normalize().multiplyScalar(4 * P);
            field.emit('smoke', { at: w, vel: aft, up: pose.dir, life: 7, s0: 1.2 * P, s1: 4.5 * P, c0: '#e2dfd9', c1: '#c4c0b9', drag: 0.5, spin: 0.2 });
          }
        }
      }
    }
    for (const [id, v] of this.ships) if (!seen.has(id)) { this.scene.remove(v.root); this.ships.delete(id); }
    if (pools) for (const t of Object.keys(state.lighterStrips ?? {})) this.carry(state, Number(t), tick, groundAt, pools);
  }

  /** The side's staged frames: those waiting on the apron, the one the robots and the arm have. */
  private carry(state: GameState, team: number, tick: number, groundAt: (p: PlanetState, d: Vec3) => number, pools: MotorPools): void {
    const e = pools.entry(team);
    if (!e) return;
    const queue = state.lighters?.staged?.[team] ?? [], runs = state.lighters?.runs ?? [], frameTicks = ticksOf(LIGHTER.frameOutSeconds);
    let c = this.carries.get(team);
    if (!queue.length && !c) return;
    if (!c) {
      const kit = this.kit(team), arm = new THREE.Group();
      arm.name = `lighter:arm:${team}`;
      const bots = [{ lift: 0, spread: 1 }, { lift: 0 }, { lift: 0.6 }].map(o => { const b = buildHandler('corner', kit.p, kit.livery, o).mesh; b.name = 'lighter:bots'; b.visible = false; e.g.add(b); return b; });
      const st = planCentre(MOTOR_POOL, MOTOR_POOL.arm.i, MOTOR_POOL.arm.j), armAt = new THREE.Vector3(st.x * U, 0, st.z * U - 1);
      e.g.add(arm);
      c = { bots, arm, armMesh: null, frame: null, item: -1, path: [], armPath: [], armAt, armYaw: 0, work: null, poses: null, apron: new Map(), spots: new Map() };
      this.carries.set(team, c);
      e.look.arm.visible = false;
      this.setArm(c, team, buildHandler('arm', kit.p, kit.livery, { lift: 2.2, reach: 4.6 }).mesh.geometry);
    }
    const cv = c;
    // the apron's waiting frames, each at its spot (its index in its run, kept once the run is gone)
    const spotOf = (f: { out: number }, i: number) => {
      const r = runs.find(x => x.team === team && f.out > x.rampTick && f.out <= x.unloadedTick);
      const k = r ? Math.round((f.out - r.rampTick) / frameTicks) - 1 : cv.spots.get(f.out) ?? i % 4;
      cv.spots.set(f.out, k);
      return k;
    };
    const waiting = new Set<number>();
    queue.forEach((f, i) => {
      if (tick >= f.lift) return;
      waiting.add(f.out);
      let g = cv.apron.get(f.out);
      if (!g) { g = this.frameOf(team, f.kind); g.name = `lighter:apron:${team}`; e.g.add(g); cv.apron.set(f.out, g); }
      const k = spotOf(f, i), w = this.onStrip(state, team, apronSpot(k).x, apronSpot(k).z, groundAt);
      if (w) g.position.copy(e.g.worldToLocal(w.clone()));
      g.rotation.set(0, 0, 0);
    });
    for (const [out, g] of cv.apron) if (!waiting.has(out)) { e.g.remove(g); cv.apron.delete(out); }
    // the one in hand: its path made when its carry starts
    const i = queue.findIndex(f => tick >= f.lift && tick < f.stacked), f = i >= 0 ? queue[i] : null;
    if (!f) {
      if (cv.frame) { e.g.remove(cv.frame); cv.frame = null; pools.refresh(state, 0, true); }
      cv.bots.forEach(b => { b.visible = false; });
      return;
    }
    if (cv.item !== f.out) {
      if (cv.frame) { e.g.remove(cv.frame); pools.refresh(state, 0, true); }
      cv.item = f.out;
      cv.frame = this.frameOf(team, f.kind); cv.frame.name = `lighter:carried:${team}`; e.g.add(cv.frame);
      const spot = nextFreeSpot(MOTOR_POOL, e.stacks) ?? { slot: 0, tier: 0 };
      cv.work = armWork(MOTOR_POOL, spot);
      cv.poses = null;
      cv.path = this.walkPath(state, team, spotOf(f, i), cv.work, groundAt, e.g);
      const via = (z: number) => new THREE.Vector3(0, 0, z);
      cv.armPath = Math.abs(cv.armAt.z - cv.work.pedestal.z) < 1 ? [cv.armAt.clone(), cv.work.pedestal.clone()] : [cv.armAt.clone(), via(cv.armAt.z), via(cv.work.pedestal.z), cv.work.pedestal.clone()];
      (cv.frame as THREE.Group & { spot?: unknown }).spot = spot;
    }
    const s = carryStage(f, tick), w = cv.work!, frame = cv.frame!;
    const show = (k: number) => cv.bots.forEach((b, j) => { b.visible = j === k; });
    const botsAt = (p: THREE.Vector3) => cv.bots.forEach(b => { b.position.copy(p); b.rotation.set(0, 0, 0); });
    if (s.stage === 'pick') {
      const p = cv.path[0];
      show(s.share < 0.45 ? 0 : s.share < 0.75 ? 1 : 2); botsAt(p);
      frame.position.copy(p).setY(p.y + (s.share < 0.75 ? 0 : 0.6)); frame.rotation.set(0, 0, 0);
    } else if (s.stage === 'walk') {
      const p = alongPolyline(cv.path, s.share), setting = s.share > 0.95;
      show(setting ? 1 : 2); botsAt(p);
      frame.position.copy(p).setY(p.y + (setting ? 0 : 0.6)); frame.rotation.set(0, 0, 0);
      // the arm drives to its work meanwhile, turned along its way, then toward the drop
      const a = alongPolyline(cv.armPath, Math.min(1, s.share * 1.25)), d = a.clone().sub(cv.armAt);
      if (d.lengthSq() > 1e-4) cv.armYaw = yawOf(d);
      if (s.share * 1.25 >= 1) cv.armYaw = yawOf(w.drop.clone().sub(w.pedestal));
      cv.armAt.copy(a);
      cv.arm.position.copy(cv.armAt); cv.arm.rotation.y = cv.armYaw;
    } else if (s.stage === 'stack') {
      show(0); cv.bots.forEach(b => { b.visible = s.share < 0.2; });
      botsAt(w.drop);
      if (!cv.poses) {
        const kit = this.kit(team), spot = (frame as THREE.Group & { spot?: { tier: number } }).spot ?? { tier: 0 }, top = (n: number) => n * FRAME.height + 0.02 + FRAME.height;
        cv.poses = {
          low: buildHandler('arm', kit.p, kit.livery, { lift: FRAME.height + 0.02, reach: REACH }).mesh.geometry,
          high: buildHandler('arm', kit.p, kit.livery, { lift: top(spot.tier) + 0.6, reach: REACH }).mesh.geometry,
          set: buildHandler('arm', kit.p, kit.livery, { lift: top(spot.tier), reach: REACH }).mesh.geometry,
        };
      }
      const spot = (frame as THREE.Group & { spot?: { tier: number } }).spot ?? { tier: 0 }, top = spot.tier * FRAME.height + 0.02 + FRAME.height;
      const toDrop = yawOf(w.drop.clone().sub(w.pedestal)), toT = yawOf(w.face), x = s.share;
      const yaw = x < 0.4 ? toDrop : x < 0.7 ? toDrop + angleTo(toDrop, toT) * ((x - 0.4) / 0.3) : toT;
      const lift = x < 0.15 ? FRAME.height + 0.02 : x < 0.7 ? top + 0.6 : top;
      this.setArm(cv, team, x < 0.15 ? cv.poses.low : x < 0.7 ? cv.poses.high : cv.poses.set);
      cv.arm.position.copy(w.pedestal); cv.arm.rotation.y = yaw;
      // the frame hangs crosswise under the spreader, `REACH` out along the arm's facing
      const facing = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
      frame.position.copy(w.pedestal).addScaledVector(facing, REACH).setY(x < 0.15 ? 0 : lift - FRAME.height);
      frame.rotation.set(0, yaw + Math.PI / 2, 0);
      cv.armAt.copy(w.pedestal); cv.armYaw = yaw;
    }
  }

  /** The arm drawn in `geo` (the pedestal at its group's origin: the builder's base sits 1 m behind its pedestal's line). */
  private setArm(c: CarryView, team: number, geo: THREE.BufferGeometry): void {
    if (c.armMesh && c.armMesh.geometry === geo) return;
    if (!c.armMesh) { c.armMesh = new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ vertexColors: true })); c.armMesh.position.z = 1.0; c.arm.add(c.armMesh); c.arm.position.copy(c.armAt); return; }
    c.armMesh.geometry = geo;
    void team;
  }

  /** The robots' way from the apron spot to the drop (the yard's frame): round the strip's side, in by the back gate, down the passage, along the lane. */
  private walkPath(state: GameState, team: number, k: number, w: ReturnType<typeof armWork>, groundAt: (p: PlanetState, d: Vec3) => number, g: THREE.Group): THREE.Vector3[] {
    const out: THREE.Vector3[] = [], side = 22, front = LIGHTER.strip.alongPeople / 2 * U - 4;
    const local = (x: number, z: number) => { const p = this.onStrip(state, team, x, z, groundAt); return p ? g.worldToLocal(p.clone()) : null; };
    // on the strip and over the ground to the back gate, a point every ~6 m (each dropped onto the drawn ground)
    const legs: Array<[number, number]> = [[apronSpot(k).x, apronSpot(k).z], [side, apronSpot(k).z], [side, front]];
    for (let i = 0; i < legs.length; i++) { const p = local(legs[i][0], legs[i][1]); if (p) out.push(p); }
    const gate = (MOTOR_POOL.rows / 2) * 2 * U, gateX = (8 - MOTOR_POOL.cols / 2) * 2 * U, last = out[out.length - 1] ?? new THREE.Vector3(gateX, 0, gate + 20);
    const outside = new THREE.Vector3(gateX, 0, gate + 6), n = Math.max(1, Math.ceil(last.distanceTo(outside) / 6));
    for (let i = 1; i <= n; i++) {
      const p = last.clone().lerp(outside, i / n), wpt = g.localToWorld(p.clone()), planet = state.planets[state.lighterStrips![team].planetIndex];
      const d = wpt.clone().sub(new THREE.Vector3(planet.position.x, planet.position.y, planet.position.z)).normalize();
      const ground = new THREE.Vector3(planet.position.x, planet.position.y, planet.position.z).addScaledVector(d, groundAt(planet, { x: d.x, y: d.y, z: d.z }));
      const lp = g.worldToLocal(ground);
      out.push(Math.hypot(p.x, p.z) < gate + 3 ? p.setY(0) : lp);
    }
    // inside: through the gate, down the passage to the lane, along it to the drop
    out.push(new THREE.Vector3(gateX, 0, gate - 2), new THREE.Vector3(0, 0, gate - 4), new THREE.Vector3(0, 0, w.drop.z), w.drop.clone());
    return out;
  }

  dispose(): void {
    for (const v of this.ships.values()) this.scene.remove(v.root);
    for (const ms of this.strips.values()) for (const m of ms) { this.scene.remove(m); m.geometry.dispose(); (m.material as THREE.Material).dispose(); }
    for (const c of this.carries.values()) { c.arm.removeFromParent(); c.frame?.removeFromParent(); c.bots.forEach(b => b.removeFromParent()); c.apron.forEach(g => g.removeFromParent()); }
    for (const h of this.hulls.values()) { h.full.dispose(); h.far.dispose(); h.nacelle.dispose(); h.nacelleFar.dispose(); h.mat.dispose(); }
    for (const k of this.kits.values()) { k.frame.geo.dispose(); k.frame.mat.dispose(); for (const c of k.cars.values()) { c.geo.dispose(); c.mat.dispose(); } }
    this.plumes.jets.dispose(); this.plumes.bells.dispose(); this.plumes.jetMat.dispose(); this.plumes.bellMat.dispose(); this.plumes.ramp.dispose(); this.plumes.rampMat.dispose();
    this.ships.clear(); this.strips.clear(); this.carries.clear();
  }
}

/** The shortest turn from yaw `a` to yaw `b` (radians). */
function angleTo(a: number, b: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}
